import React, { useEffect, useState } from "react";
import { formatPrice } from "../../lib/format";
import { api } from "../../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function AdminAbandonedCarts() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // cart id currently being reminded

  const refresh = () => {
    setLoading(true);
    api.get("/admin/abandoned-carts")
      .then(r => setCarts(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce panier de la liste ?")) return;
    try {
      await api.delete(`/admin/abandoned-carts/${id}`);
      setCarts(c => c.filter(x => x.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remind = async (cart) => {
    if (sending) return;
    setSending(cart.id);
    try {
      const { data } = await api.post(`/admin/abandoned-carts/${cart.id}/remind`);
      toast.success(`Email envoyé à ${data.sent_to} ✿`);
      setCarts(cs => cs.map(c => c.id === cart.id
        ? { ...c, last_reminded_at: new Date().toISOString(), reminder_count: (c.reminder_count || 0) + 1 }
        : c
      ));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <p className="overline text-berulia-rosedeep">BERULIA · Back-office</p>
      <h1 className="font-heading text-3xl sm:text-4xl mt-2">Paniers abandonnés</h1>
      <p className="text-sm text-berulia-inkmuted mt-2">
        Paniers commencés au checkout (email saisi) mais non payés. Auto-purgés après 14 jours.
      </p>

      <div className="bg-white border border-berulia-border mt-8 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-berulia-inkmuted">Chargement…</div>
        ) : carts.length === 0 ? (
          <div className="p-8 text-berulia-inkmuted">Aucun panier abandonné.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-berulia-border bg-berulia-beigewarm">
                <th className="px-4 py-3 overline">Date</th>
                <th className="px-4 py-3 overline">Cliente</th>
                <th className="px-4 py-3 overline">Email</th>
                <th className="px-4 py-3 overline">Articles</th>
                <th className="px-4 py-3 overline">Total</th>
                <th className="px-4 py-3"/>
              </tr>
            </thead>
            <tbody>
              {carts.map(c => (
                <tr key={c.id} className="border-b border-berulia-border hover:bg-berulia-beigewarm/40">
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3">{c.customer_name || "—"}</td>
                  <td className="px-4 py-3">
                    <a href={`mailto:${c.customer_email}`} className="hover:text-berulia-rosedeep">{c.customer_email}</a>
                  </td>
                  <td className="px-4 py-3">
                    <details>
                      <summary className="cursor-pointer">{(c.items || []).length} article{(c.items || []).length > 1 ? "s" : ""}</summary>
                      <ul className="mt-2 space-y-1 text-xs text-berulia-inkmuted">
                        {(c.items || []).map((it, i) => (
                          <li key={i}>· {it.name} ({it.size}) × {it.quantity} — {formatPrice((it.unit_price * it.quantity))}</li>
                        ))}
                      </ul>
                    </details>
                  </td>
                  <td className="px-4 py-3 font-heading">{formatPrice((c.subtotal || 0))}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => remind(c)}
                        disabled={sending === c.id}
                        data-testid={`cart-remind-${c.id}`}
                        className="text-xs bg-berulia-rosedeep text-berulia-inkdeep overline px-3 py-1.5 hover:bg-berulia-rose disabled:opacity-50"
                        title={c.last_reminded_at ? `Dernière relance : ${fmtDate(c.last_reminded_at)}` : "Envoyer un rappel par email"}
                      >
                        {sending === c.id ? "Envoi…" : "Relancer"}
                      </button>
                      {c.reminder_count > 0 && (
                        <span className="text-[10px] text-berulia-inkmuted" title={`Relancé ${c.reminder_count} fois`}>
                          ×{c.reminder_count}
                        </span>
                      )}
                      <button
                        onClick={() => remove(c.id)}
                        className="text-xs text-berulia-inkmuted hover:text-berulia-inkdeep"
                        data-testid={`cart-remove-${c.id}`}
                      >Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
